import { useCallback, useEffect, useState } from 'react'

import {
  getDealListingNotifications,
  viewDealListingNotification,
} from '@/lib/api'
import { useSession } from '@/lib/session-context'

const POLLING_INTERVAL = 30_000

export function useListingNotifications({ enabled = true } = {}) {
  const { csrfToken, currentUser } = useSession()
  const [inbox, setInbox] = useState({ notifications: [], unreadCount: 0 })
  const [isLoading, setIsLoading] = useState(Boolean(enabled && currentUser))
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!enabled || !currentUser) return undefined

    let active = true

    const load = async () => {
      try {
        const payload = await getDealListingNotifications({ csrfToken })
        if (!active) return

        setInbox({
          notifications: payload.notifications ?? [],
          unreadCount: payload.unreadCount ?? 0,
        })
        setError(null)
      } catch (loadError) {
        if (active) setError(loadError.message)
      } finally {
        if (active) setIsLoading(false)
      }
    }

    const handleFocus = () => { void load() }
    void load()
    window.addEventListener('focus', handleFocus)
    const intervalId = window.setInterval(load, POLLING_INTERVAL)

    return () => {
      active = false
      window.removeEventListener('focus', handleFocus)
      window.clearInterval(intervalId)
    }
  }, [csrfToken, currentUser, enabled])

  const viewNotification = useCallback(async (notificationId) => {
    const payload = await viewDealListingNotification(notificationId, { csrfToken })
    setInbox((current) => {
      const wasUnread = current.notifications.some((item) => (
        item.id === notificationId && item.unread
      ))

      return {
        notifications: current.notifications.map((item) => (
          item.id === notificationId ? payload.notification : item
        )),
        unreadCount: wasUnread ? Math.max(0, current.unreadCount - 1) : current.unreadCount,
      }
    })
    return payload
  }, [csrfToken])

  return {
    ...inbox,
    error,
    isLoading,
    viewNotification,
  }
}
